import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { combineLatest, merge, Observable } from 'rxjs';
import { filter, map, reduce, switchMap } from 'rxjs/operators';

import { LocationRouteCffService } from '@fcom/core';
import { BoundType, LocationInfoMap } from '@fcom/dapi';
import { FinnairBoundItem, FinnairLocation, FinnairLocationType } from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate';
import { getKeyCodeFromEvent, KeyCodes, LocalDate, unique } from '@fcom/core/utils';
import { BUS_AIRCRAFT_CODE } from '@fcom/common-booking/interfaces';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';

import { TimetableLocationsService } from '../../services/timetable-locations.service';
import { SearchState, TimetableFeatureState, TimetableScheduleItem, TimetableStatus } from '../../interfaces';
import { selectData, selectSearch, selectStatus } from '../../store/selectors';

@Component({
  selector: 'fin-timetable-schedule',
  templateUrl: './timetable-schedule.component.html',
  styleUrls: ['timetable-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimetableScheduleComponent implements OnInit {
  public readonly BoundType = BoundType;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  bounds$: Observable<FinnairBoundItem[]>;
  items$: Observable<TimetableScheduleItem[]>;
  selectedBound$: Observable<FinnairBoundItem>;
  locations$: Observable<LocationInfoMap>;
  search$: Observable<SearchState>;
  loading$: Observable<boolean>;
  detailsModalOpen = false;

  constructor(
    private store: Store<TimetableFeatureState>,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private timetableLocations: TimetableLocationsService
  ) {}

  ngOnInit(): void {
    this.search$ = this.store.pipe(selectSearch());
    this.loading$ = this.store.pipe(
      selectStatus(),
      map((state) => state.status === TimetableStatus.LOADING)
    );
    this.bounds$ = combineLatest([this.store.pipe(selectData()), this.search$]).pipe(
      filter(([data]) => {
        return !!data?.flights;
      }),
      map(([{ flights }, search]) => {
        const searchDate = search.selectedDay.toString();

        if (!flights || !flights[searchDate]) {
          return [];
        }

        return flights[searchDate];
      })
    );
    this.items$ = this.bounds$.pipe(
      map((bounds) =>
        bounds.map((bound) => {
          return {
            flights: bound.itinerary.filter(isFlight).map((flight) => {
              return {
                flightNumber: flight.flightNumber,
                departureTime: this.getTime(flight.departure.dateTime),
                arrivalTime: this.getTime(flight.arrival.dateTime),
                overnight: this.isOvernight(flight.departure.dateTime, flight.arrival.dateTime),
                icon:
                  flight.aircraft?.code === BUS_AIRCRAFT_CODE ? SvgLibraryIcon.BUS : SvgLibraryIcon.AIRCRAFT_HORIZONTAL,
              };
            }),
            duration: bound.duration,
            origin$: this.timetableLocations.getCityNameForLocationCode(bound.departure.locationCode),
            destination$: this.timetableLocations.getCityNameForLocationCode(bound.arrival.locationCode),
            departureTime: this.getTime(bound.departure.dateTime),
            arrivalTime: this.getTime(bound.arrival.dateTime),
          };
        })
      )
    );
  }

  openModal(event: MouseEvent | KeyboardEvent, item: TimetableScheduleItem): void {
    event.stopPropagation();

    this.selectedBound$ = this.bounds$.pipe(
      map((bounds) =>
        bounds.find(
          (bound) =>
            bound.itinerary
              .filter(isFlight)
              .map((flight) => flight.flightNumber)
              .join(',') === item.flights.map((flight) => flight.flightNumber).join(',')
        )
      )
    );
    this.locations$ = this.selectedBound$.pipe(
      filter(Boolean),
      map((bound: FinnairBoundItem) => {
        return bound.itinerary
          .filter(isFlight)
          .reduce((locationCodes, flight) => {
            return locationCodes.concat([flight.departure.locationCode, flight.arrival.locationCode]);
          }, [])
          .filter(unique);
      }),
      switchMap((locationCodes: string[]) => {
        return this.getLocations(locationCodes);
      })
    );
    this.detailsModalOpen = true;
  }

  closeModal(): void {
    this.detailsModalOpen = false;
  }

  rowKeyPress(keyboardEvent: KeyboardEvent, item: TimetableScheduleItem): void {
    if (getKeyCodeFromEvent(keyboardEvent).keyCode !== KeyCodes.ENTER.keyCode) {
      return;
    }

    this.openModal(keyboardEvent, item);
  }

  private getLocations(locationCodes: string[]): Observable<LocationInfoMap> {
    const locations: Observable<FinnairLocation>[] = locationCodes.map((locationCode) =>
      this.getLocation(locationCode)
    );
    return merge(...locations).pipe(
      reduce((acc, location) => {
        acc[location.cityCode] = location;
        return acc;
      }, {} as LocationInfoMap)
    );
  }

  private getLocation(locationCode: string): Observable<FinnairLocation> {
    return this.locationRouteCffService.bestGuessFor(locationCode, this.languageService.localeValue).pipe(
      map((location) => {
        /**
         * TODO: remove the casting when backend FinnairPicture is fixed
         */
        return {
          ...location,
          type: location.type === 'airport' ? FinnairLocationType.AIRPORT : FinnairLocationType.CITY,
          cityCode: locationCode,
          name: location.title,
        } as unknown as FinnairLocation;
      })
    );
  }

  private isOvernight(departureTime: string, arrivalTime: string): boolean {
    const departureDate: LocalDate = new LocalDate(this.getDate(departureTime));
    const arrivalDate: LocalDate = new LocalDate(this.getDate(arrivalTime));
    const nextDay: LocalDate = departureDate.plusDays(1);
    return (
      arrivalDate.localDay === nextDay.localDay &&
      arrivalDate.localMonth === nextDay.localMonth &&
      arrivalDate.localYear === nextDay.localYear
    );
  }

  private getDate(dateTime: string): string {
    return dateTime.substring(0, dateTime.indexOf('T'));
  }

  private getTime(dateTime: string): string {
    return dateTime.substr(dateTime.indexOf('T') + 1, 5);
  }
}
